import { type ReactNode, type ReactElement, isValidElement, cloneElement } from 'react';

import { useDropdown } from './context';

export interface DropdownTriggerRenderProps {
  isOpen: boolean;
}

export interface DropdownTriggerProps {
  /**
   * Content for the trigger. Can be:
   * - A React element (Button, IconButton) - will clone and add ref/props
   * - Text/nodes - will render inside default styled button
   * - A render function for dynamic content
   */
  children?: ReactNode | ((context: DropdownTriggerRenderProps) => ReactNode);
  /** Placeholder when no children */
  placeholder?: string;
  /** Whether the trigger is disabled (used only for default button, custom elements handle their own disabled) */
  disabled?: boolean;
  /** Additional className (used only for default button) */
  className?: string;
  /**
   * Use custom element as trigger (pass a Button, IconButton, etc).
   * When true, children is expected to be a single React element that will receive ref and props.
   */
  asChild?: boolean;
}

/**
 * DropdownTrigger - renders the trigger button that opens the dropdown
 *
 * @example
 * ```tsx
 * // Simple text (uses default styled button)
 * <DropdownTrigger>Actions</DropdownTrigger>
 *
 * // With custom Button (asChild clones the element)
 * <DropdownTrigger asChild>
 *   <Button variant="outline" size="sm">
 *     Actions <ChevronIcon />
 *   </Button>
 * </DropdownTrigger>
 *
 * // With render function for dynamic content
 * <DropdownTrigger>
 *   {({ isOpen }) => (
 *     <>
 *       <MenuIcon />
 *       <span>Menu</span>
 *       <ChevronIcon className={isOpen ? 'rotate-180' : ''} />
 *     </>
 *   )}
 * </DropdownTrigger>
 * ```
 */
export function DropdownTrigger({
  children,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  asChild = false,
}: DropdownTriggerProps) {
  const { isOpen, size, setReference, getReferenceProps } = useDropdown();

  const renderProps: DropdownTriggerRenderProps = {
    isOpen,
  };

  // Handle asChild mode - clone the child element with ref and props
  if (asChild && isValidElement(children)) {
    return cloneElement(children as ReactElement<Record<string, unknown>>, {
      ref: setReference,
      ...getReferenceProps(),
    });
  }

  // Size classes for default trigger button
  // sm: padding left/right 12px (pb-3), top/bottom 8px (pb-2)
  // md: padding left/right 14px (pb-3.5), top/bottom 10px (pb-2.5)
  const sizeClasses =
    size === 'sm'
      ? 'px-pb-3 py-pb-2 text-pb-xs min-h-[36px]'
      : 'px-pb-3.5 py-pb-2.5 text-pb-sm min-h-[42px]';

  const content =
    typeof children === 'function'
      ? children(renderProps)
      : children || <span className="text-gray-500">{placeholder}</span>;

  return (
    <button
      ref={setReference}
      type="button"
      disabled={disabled}
      className={`
        inline-flex items-center gap-pb-2 border border-gray-300 rounded-pb-2 bg-white
        hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-uiv2-primary-500 focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300
        ${sizeClasses}
        ${className}
      `}
      {...getReferenceProps()}
    >
      <span className="flex-1 flex items-center gap-pb-2 min-w-0">{content}</span>
      <svg
        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}
